import { useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { languageData } from "../../shared/utils/utils";

const BASE_URL_DEFAULT = (
  process.env.REACT_APP_API_BACKEND || "https://backend.com"
).replace(/\/$/, "");

function formatBodyForDisplay(body) {
  if (body == null) return null;
  if (typeof body !== "string") return JSON.stringify(body, null, 2);
  try {
    const parsed = JSON.parse(body);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return body;
  }
}

function getFieldDescriptions(t) {
  return {
    token: {
      access_token: t("tpa_desc_access_token"),
      token_type: t("tpa_desc_token_type"),
      expires_in: t("tpa_desc_expires_in"),
    },
    containers: {
      containers: t("tpa_desc_containers"),
      count: t("tpa_desc_count"),
    },
    containerItem: {
      containerMongoId: t("tpa_desc_containerMongoId"),
      containerId: t("tpa_desc_containerId"),
      corporationName: t("tpa_desc_corporationName"),
      positioning: t("tpa_desc_positioning"),
      address: t("tpa_desc_address"),
      fillingLevel: t("tpa_desc_fillingLevel"),
      latestImageUrl: t("tpa_desc_latestImageUrl"),
    },
    imageUrlsItem: {
      containerMongoId: t("tpa_desc_containerMongoId"),
      containerId: t("tpa_desc_containerId"),
      latestImageUrl: t("tpa_desc_latestImageUrl"),
    },
    rotate: {
      client_id: t("tpa_desc_rotate_client_id"),
      client_secret: t("tpa_desc_rotate_client_secret"),
      message: t("tpa_desc_rotate_message"),
    },
  };
}

function RequestBlock({ request, headersLabel, bodyLabel }) {
  if (!request) return null;
  const bodyFormatted =
    request.body != null ? formatBodyForDisplay(request.body) : null;
  return (
    <div className="tpa-request-block">
      <div className="tpa-request-line">
        <span className="tpa-method">{request.method}</span> {request.url}
      </div>
      <div className="tpa-headers">
        <span className="tpa-label">{headersLabel}</span>
        <pre className="tpa-code">
          {JSON.stringify(request.headers, null, 2)}
        </pre>
      </div>
      {bodyFormatted != null && (
        <div className="tpa-body">
          <span className="tpa-label">{bodyLabel}</span>
          <pre className="tpa-code">{bodyFormatted}</pre>
        </div>
      )}
    </div>
  );
}

function ResponseRaw({ data, error, status, ok }) {
  if (error) {
    return (
      <div className="tpa-response-raw tpa-response-error">
        <pre>{error}</pre>
      </div>
    );
  }
  return (
    <div className="tpa-response-raw">
      {status != null && (
        <p className={`tpa-status ${ok ? "tpa-status-ok" : "tpa-status-err"}`}>
          HTTP {status}
        </p>
      )}
      <pre className="tpa-code">{JSON.stringify(data, null, 2)}</pre>
    </div>
  );
}

function ResponseDecoded({
  data,
  error,
  descriptions,
  itemDescriptions,
  arrayKey,
}) {
  if (error || !data) return null;
  const entries = Object.entries(data).filter(
    ([k]) => k !== "containers" || !arrayKey,
  );
  const list =
    arrayKey && Array.isArray(data[arrayKey]) ? data[arrayKey] : null;

  return (
    <div className="tpa-response-decoded">
      {entries.map(([key, value]) => {
        const desc = descriptions[key];
        const valStr =
          typeof value === "object" ? JSON.stringify(value) : String(value);
        return (
          <div key={key} className="tpa-decoded-row">
            <span className="tpa-decoded-key">{key}</span>
            <span className="tpa-decoded-value" title={valStr}>
              {valStr.length > 80 ? valStr.slice(0, 80) + "…" : valStr}
            </span>
            {desc && <span className="tpa-decoded-desc">{desc}</span>}
          </div>
        );
      })}
      {list && list.length > 0 && itemDescriptions && (
        <div className="tpa-decoded-list">
          <span className="tpa-label">
            {arrayKey} ({list.length} élément(s))
          </span>
          {list.map((item, idx) => (
            <div key={idx} className="tpa-decoded-item">
              {Object.entries(item).map(([k, v]) => {
                const d = itemDescriptions[k];
                const vs =
                  typeof v === "object" ? JSON.stringify(v) : String(v);
                return (
                  <div key={k} className="tpa-decoded-row">
                    <span className="tpa-decoded-key">{k}</span>
                    <span className="tpa-decoded-value" title={vs}>
                      {vs.length > 60 ? vs.slice(0, 60) + "…" : vs}
                    </span>
                    {d && <span className="tpa-decoded-desc">{d}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ThirdPartyApi = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const fieldDescriptions = useMemo(() => getFieldDescriptions(t), [t]);
  const [baseUrl, setBaseUrl] = useState(BASE_URL_DEFAULT);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [token, setToken] = useState("");
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [tokenRequest, setTokenRequest] = useState(null);
  const [tokenResponse, setTokenResponse] = useState(null);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);

  const toggleLanguageDropdown = () => setLanguageDropdownOpen((prev) => !prev);
  const changeLanguage = (lang) => {
    localStorage.setItem("language", lang);
    i18n.changeLanguage(lang);
    setLanguageDropdownOpen(false);
  };
  const [containersReq, setContainersReq] = useState(null);
  const [containersRes, setContainersRes] = useState(null);
  const [containersLoading, setContainersLoading] = useState(false);
  const [imageUrlsReq, setImageUrlsReq] = useState(null);
  const [imageUrlsRes, setImageUrlsRes] = useState(null);
  const [imageUrlsLoading, setImageUrlsLoading] = useState(false);
  const [rotateReq, setRotateReq] = useState(null);
  const [rotateRes, setRotateRes] = useState(null);
  const [rotateLoading, setRotateLoading] = useState(false);

  const getToken = async () => {
    setTokenError("");
    setToken("");
    setTokenResponse(null);
    const url = `${baseUrl}/oauth/token`;
    const body = {
      client_id: clientId,
      client_secret: clientSecret,
      grant_type: "client_credentials",
    };
    const bodyStr = JSON.stringify(body);
    setTokenRequest({
      method: "POST",
      url,
      headers: { "Content-Type": "application/json" },
      body: bodyStr,
    });
    setTokenLoading(true);
    try {
      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: bodyStr,
      });
      const data = await r.json().catch(() => ({}));
      setTokenResponse({ ok: r.ok, status: r.status, data });
      if (!r.ok) {
        setTokenError(
          data.error_description || data.error || r.statusText || "Erreur",
        );
        return;
      }
      setToken(data.access_token || "");
    } catch (e) {
      setTokenError(e.message || "");
      setTokenResponse({ error: e.message || "Erreur réseau" });
    } finally {
      setTokenLoading(false);
    }
  };

  const testContainers = async () => {
    if (!token) {
      setContainersRes({ error: t("tpa_get_token_first") });
      return;
    }
    const url = `${baseUrl}/api/oauth2/third-party/containers`;
    setContainersReq({
      method: "GET",
      url,
      headers: { Authorization: `Bearer ${token}` },
      body: null,
    });
    setContainersRes(null);
    setContainersLoading(true);
    try {
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      setContainersRes({ ok: r.ok, status: r.status, data });
    } catch (e) {
      setContainersRes({ error: e.message || "Erreur réseau" });
    } finally {
      setContainersLoading(false);
    }
  };

  const testImageUrls = async () => {
    if (!token) {
      setImageUrlsRes({ error: t("tpa_get_token_first") });
      return;
    }
    const url = `${baseUrl}/api/oauth2/third-party/containers/image-urls`;
    setImageUrlsReq({
      method: "GET",
      url,
      headers: { Authorization: `Bearer ${token}` },
      body: null,
    });
    setImageUrlsRes(null);
    setImageUrlsLoading(true);
    try {
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      setImageUrlsRes({ ok: r.ok, status: r.status, data });
    } catch (e) {
      setImageUrlsRes({ error: e.message || "Erreur réseau" });
    } finally {
      setImageUrlsLoading(false);
    }
  };

  const testRotate = async () => {
    const url = `${baseUrl}/oauth/clients/rotate-secret`;
    const body = { client_id: clientId, client_secret: clientSecret };
    setRotateReq({
      method: "POST",
      url,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    setRotateRes(null);
    setRotateLoading(true);
    try {
      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      setRotateRes({ ok: r.ok, status: r.status, data });
    } catch (e) {
      setRotateRes({ error: e.message || "Erreur réseau" });
    } finally {
      setRotateLoading(false);
    }
  };

  const backArrowSvg = (
    <svg
      width="16"
      height="12"
      viewBox="0 0 16 12"
      fill="none"
      className="tpa-back-arrow"
    >
      <path
        d="M6 12L0 6L6 0L7.4 1.45L3.85 5H16V7H3.85L7.4 10.55L6 12Z"
        fill="currentColor"
      />
    </svg>
  );

  return (
    <div className="base tpa-page">
      <div className="tpa-center">
        <button
          type="button"
          className="tpa-back"
          onClick={() => history.push("/login")}
        >
          {backArrowSvg}
          <span>{t("Back to login")}</span>
        </button>
        <header className="tpa-header">
          <h1>{t("tpa_title")}</h1>
          <p className="tpa-subtitle">{t("tpa_subtitle")}</p>
        </header>

        <section className="tpa-card tpa-intro">
          <p>{t("tpa_intro_1")}</p>
          <p>{t("tpa_intro_2")}</p>
          <p>{t("tpa_intro_3")}</p>
        </section>

        <section className="tpa-card tpa-section">
          <h2 className="tpa-section-title">{t("tpa_configuration")}</h2>
          <div className="tpa-form-row">
            <label>{t("tpa_base_url")}</label>
            <input
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder={BASE_URL_DEFAULT}
            />
          </div>
          <div className="tpa-form-row">
            <label>{t("tpa_client_id")}</label>
            <input
              type="text"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={t("tpa_placeholder_client_id")}
            />
          </div>
          <div className="tpa-form-row">
            <label>{t("tpa_client_secret")}</label>
            <input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={t("tpa_placeholder_client_secret")}
            />
          </div>
        </section>

        <section className="tpa-card tpa-section">
          <h2 className="tpa-section-title">{t("tpa_auth_title")}</h2>
          <p className="tpa-desc">{t("tpa_auth_desc")}</p>
          <button
            type="button"
            className="tpa-btn"
            onClick={getToken}
            disabled={tokenLoading || !clientId || !clientSecret}
          >
            {tokenLoading ? t("tpa_loading") : t("tpa_get_token")}
          </button>
          {tokenRequest && (
            <div className="tpa-block tpa-block-request">
              <h3 className="tpa-block-title">{t("tpa_request_sent")}</h3>
              <RequestBlock
                request={tokenRequest}
                headersLabel={t("tpa_headers")}
                bodyLabel={t("tpa_body")}
              />
            </div>
          )}
          {tokenResponse && (
            <div className="tpa-block tpa-block-response">
              <h3 className="tpa-block-title">{t("tpa_response_received")}</h3>
              <div className="tpa-tabs">
                <div className="tpa-tab-content">
                  <span className="tpa-tab-label">{t("tpa_raw_response")}</span>
                  <ResponseRaw
                    data={tokenResponse.data}
                    error={tokenResponse.error}
                    status={tokenResponse.status}
                    ok={tokenResponse.ok}
                  />
                </div>
                <div className="tpa-tab-content">
                  <span className="tpa-tab-label">
                    {t("tpa_fields_explained")}
                  </span>
                  <ResponseDecoded
                    data={tokenResponse.data}
                    error={tokenResponse.error}
                    descriptions={fieldDescriptions.token}
                  />
                </div>
              </div>
            </div>
          )}
          {tokenError && <p className="tpa-error">{tokenError}</p>}
          {tokenError && tokenError.toLowerCase().includes("fetch") && (
            <div className="tpa-callout tpa-callout-cors">
              {t("tpa_cors_callout")}
            </div>
          )}
          {token && <p className="tpa-success">{t("tpa_token_success")}</p>}
        </section>

        <section className="tpa-card tpa-section">
          <h2 className="tpa-section-title">{t("tpa_containers_title")}</h2>
          <p className="tpa-desc">{t("tpa_containers_intro")}</p>

          <div className="tpa-endpoint">
            <h3 className="tpa-endpoint-title">
              GET /api/oauth2/third-party/containers
            </h3>
            <p className="tpa-desc">{t("tpa_containers_full_desc")}</p>
            <button
              type="button"
              className="tpa-btn"
              onClick={testContainers}
              disabled={containersLoading}
            >
              {containersLoading ? t("tpa_loading") : t("tpa_test_route")}
            </button>
            {containersReq && (
              <div className="tpa-block tpa-block-request">
                <h3 className="tpa-block-title">{t("tpa_request_sent")}</h3>
                <RequestBlock
                  request={containersReq}
                  headersLabel={t("tpa_headers")}
                  bodyLabel={t("tpa_body")}
                />
              </div>
            )}
            {containersRes && (
              <div className="tpa-block tpa-block-response">
                <h3 className="tpa-block-title">
                  {t("tpa_response_received")}
                </h3>
                <div className="tpa-tabs">
                  <div className="tpa-tab-content">
                    <span className="tpa-tab-label">
                      {t("tpa_raw_response")}
                    </span>
                    <ResponseRaw
                      data={containersRes.data}
                      error={containersRes.error}
                      status={containersRes.status}
                      ok={containersRes.ok}
                    />
                  </div>
                  <div className="tpa-tab-content">
                    <span className="tpa-tab-label">
                      {t("tpa_fields_explained")}
                    </span>
                    <ResponseDecoded
                      data={containersRes.data}
                      error={containersRes.error}
                      descriptions={fieldDescriptions.containers}
                      itemDescriptions={fieldDescriptions.containerItem}
                      arrayKey="containers"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="tpa-endpoint">
            <h3 className="tpa-endpoint-title">
              GET /api/oauth2/third-party/containers/image-urls
            </h3>
            <p className="tpa-desc">{t("tpa_containers_images_desc")}</p>
            <button
              type="button"
              className="tpa-btn"
              onClick={testImageUrls}
              disabled={imageUrlsLoading}
            >
              {imageUrlsLoading ? t("tpa_loading") : t("tpa_test_route")}
            </button>
            {imageUrlsReq && (
              <div className="tpa-block tpa-block-request">
                <h3 className="tpa-block-title">{t("tpa_request_sent")}</h3>
                <RequestBlock
                  request={imageUrlsReq}
                  headersLabel={t("tpa_headers")}
                  bodyLabel={t("tpa_body")}
                />
              </div>
            )}
            {imageUrlsRes && (
              <div className="tpa-block tpa-block-response">
                <h3 className="tpa-block-title">
                  {t("tpa_response_received")}
                </h3>
                <div className="tpa-tabs">
                  <div className="tpa-tab-content">
                    <span className="tpa-tab-label">
                      {t("tpa_raw_response")}
                    </span>
                    <ResponseRaw
                      data={imageUrlsRes.data}
                      error={imageUrlsRes.error}
                      status={imageUrlsRes.status}
                      ok={imageUrlsRes.ok}
                    />
                  </div>
                  <div className="tpa-tab-content">
                    <span className="tpa-tab-label">
                      {t("tpa_fields_explained")}
                    </span>
                    <ResponseDecoded
                      data={imageUrlsRes.data}
                      error={imageUrlsRes.error}
                      descriptions={fieldDescriptions.containers}
                      itemDescriptions={fieldDescriptions.imageUrlsItem}
                      arrayKey="containers"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="tpa-card tpa-section tpa-rotate-section">
          <details className="tpa-rotate-details">
            <summary className="tpa-rotate-summary">
              {t("tpa_rotate_summary")}
            </summary>
            <p className="tpa-desc">{t("tpa_rotate_desc")}</p>
            <button
              type="button"
              className="tpa-btn tpa-btn-secondary"
              onClick={testRotate}
              disabled={rotateLoading || !clientId || !clientSecret}
            >
              {rotateLoading ? t("tpa_loading") : t("tpa_test_rotate")}
            </button>
            {rotateReq && (
              <div className="tpa-block tpa-block-request">
                <h3 className="tpa-block-title">{t("tpa_request_sent")}</h3>
                <RequestBlock
                  request={rotateReq}
                  headersLabel={t("tpa_headers")}
                  bodyLabel={t("tpa_body")}
                />
              </div>
            )}
            {rotateRes && (
              <div className="tpa-block tpa-block-response">
                <h3 className="tpa-block-title">
                  {t("tpa_response_received")}
                </h3>
                <div className="tpa-tabs">
                  <div className="tpa-tab-content">
                    <span className="tpa-tab-label">
                      {t("tpa_raw_response")}
                    </span>
                    <ResponseRaw
                      data={rotateRes.data}
                      error={rotateRes.error}
                      status={rotateRes.status}
                      ok={rotateRes.ok}
                    />
                  </div>
                  <div className="tpa-tab-content">
                    <span className="tpa-tab-label">
                      {t("tpa_fields_explained")}
                    </span>
                    <ResponseDecoded
                      data={rotateRes.data}
                      error={rotateRes.error}
                      descriptions={fieldDescriptions.rotate}
                    />
                  </div>
                </div>
              </div>
            )}
          </details>
        </section>

        <section className="tpa-card tpa-section tpa-errors">
          <h2 className="tpa-section-title">{t("tpa_errors_title")}</h2>
          <ul>
            <li>
              <code>401 invalid_client</code> — {t("tpa_error_401_client")}
            </li>
            <li>
              <code>401 Unauthorized</code> — {t("tpa_error_401_unauth")}
            </li>
            <li>
              <code>403 access_denied</code> / <code>403 Forbidden</code> —{" "}
              {t("tpa_error_403")}
            </li>
          </ul>
        </section>

        <footer className="tpa-footer footer">
          <div className="footer-content">
            <div className="footer-item">
              <span className="copyright-logo">Akanthas © 2024</span>
            </div>
            <div className="footer-item">
              <span
                className="footer-link"
                onClick={() => history.push("/confidentialityagreement")}
              >
                Confidentiality Agreement
              </span>
            </div>
            <div className="footer-item tpa-footer-lang">
              <span
                className="language-select"
                onClick={toggleLanguageDropdown}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && toggleLanguageDropdown()}
              >
                {t("Language")} <span className="language-arrow">▼</span>
              </span>
              <div
                id="tpa-language-dropdown"
                className={`language-dropdown ${languageDropdownOpen ? "" : "hidden"}`}
              >
                {languageData.map((language) => (
                  <div
                    key={language.value}
                    className="language-option"
                    onClick={() => changeLanguage(language.value)}
                  >
                    {language.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ThirdPartyApi;
